<template>
  <a-card :title="$t('stats.detailedStats')" :scroll="{ x: true, y: false }">
    <div
      slot="extra"
      style="margin-bottom: 16px; display: flex; gap: 16px; align-items: center"
    >
      <a-radio-group
        v-model="localLoginStatus"
        button-style="solid"
        @change="handleLoginStatusChange"
      >
        <a-radio-button value="logged_in">
          <a-icon type="check-circle" />
          {{ $t("stats.hasLoggedIn") }}
        </a-radio-button>
        <a-radio-button value="never_logged_in">
          <a-icon type="close-circle" />
          {{ $t("stats.neverLoggedIn") }}
        </a-radio-button>
      </a-radio-group>
      <a-input-search
        :value="localSearchText"
        :placeholder="$t('personnel.chercher') + ' ' + $t('menu.utilisateur')"
        style="width: 300px"
        allow-clear
        @search="handleSearch"
        @change="handleSearchChange"
        @clear="handleClear"
      />
    </div>
    <a-table
      :columns="columns"
      :data-source="rows"
      :pagination="tablePagination"
      :loading="loading"
      @change="handleTableChange"
    >
      <a slot="user" slot-scope="text">{{ text }}</a>
      <span slot="customTitleUser"
        ><a-icon type="user" /> {{ $t("menu.utilisateur") }}</span
      >
      <span slot="fullName" slot-scope="text">{{ text || "--" }}</span>
      <span slot="customTitleFullName"
        ><a-icon type="idcard" /> {{ $t("stats.fullName") }}</span
      >
      <span slot="childName" slot-scope="text">{{ text || "--" }}</span>
      <span slot="customTitleChildName"
        ><a-icon type="team" /> {{ $t("stats.childName") }}</span
      >
      <a slot="totalConnections" slot-scope="text">{{ text }}</a>
      <span slot="customTitleConnections"
        ><a-icon type="number" /> {{ $t("stats.nb_connections") }}</span
      >
      <a slot="totalDuration" slot-scope="text">{{ text }}</a>
      <span slot="customTitleDuration"
        ><a-icon type="clock-circle" /> {{ $t("stats.duration") }}</span
      >
      <a slot="lastConnection" slot-scope="text">{{ text }}</a>
      <span slot="customTitleLastConnection"
        ><a-icon type="calendar" /> {{ $t("stats.lastConnection") }}</span
      >
      <div slot="expandedRowRender" slot-scope="record">
        <div class="justify-content-center row">
          <div
            v-if="record.totalConnections === 0"
            class="col-md-10 offset-md-1 text-center"
            style="padding: 20px"
          >
            <a-empty :description="$t('stats.noConnectionHistory')" />
          </div>
          <table v-else class="table col-md-10 offset-md-1">
            <thead>
              <tr>
                <th>#</th>
                <th>{{ $t("assiduite.date") }}</th>
                <th>{{ $t("stats.connect") }}</th>
                <th>{{ $t("stats.disconnect") }}</th>
                <th>{{ $t("stats.duration") }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(session, index) in record.connectionHistory"
                :key="record.key + '_session_' + index"
              >
                <td>{{ index + 1 }}</td>
                <td>{{ moment(session.date).format("DD-MM-YYYY") }}</td>
                <td>
                  {{ moment(session.connect).format("DD-MM-YYYY HH:mm:ss") }}
                </td>
                <td>
                  {{
                    session.disconnect
                      ? moment(session.disconnect).format("DD-MM-YYYY HH:mm:ss")
                      : "--"
                  }}
                </td>
                <td>
                  {{
                    session.duration ? formatDuration(session.duration) : "0s"
                  }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </a-table>
  </a-card>
</template>
<script>
import moment from "moment";
import helpers from "../helpers";
import apiClient from "@/services/axios";

const baseColumns = [
  {
    dataIndex: "user",
    key: "user",
    slots: { title: "customTitleUser" },
    scopedSlots: { customRender: "user" },
  },
];

const parentColumns = [
  {
    dataIndex: "fullName",
    key: "fullName",
    slots: { title: "customTitleFullName" },
    scopedSlots: { customRender: "fullName" },
  },
  {
    dataIndex: "childName",
    key: "childName",
    slots: { title: "customTitleChildName" },
    scopedSlots: { customRender: "childName" },
  },
];

const statsColumns = [
  {
    dataIndex: "totalConnections",
    key: "totalConnections",
    slots: { title: "customTitleConnections" },
    scopedSlots: { customRender: "totalConnections" },
  },
  {
    dataIndex: "totalDuration",
    key: "totalDuration",
    slots: { title: "customTitleDuration" },
    scopedSlots: { customRender: "totalDuration" },
  },
  {
    dataIndex: "lastConnection",
    key: "lastConnection",
    slots: { title: "customTitleLastConnection" },
    scopedSlots: { customRender: "lastConnection" },
  },
];

export default {
  props: {
    pagination: {
      type: Object,
      default: () => ({
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: (total, range) =>
          this.$t("all.pagination.showTotal", {
            start: range[0],
            end: range[1],
            total: total,
          }),
        pageSizeOptions: ["10", "20", "50", "100"],
      }),
    },
    searchText: {
      type: String,
      default: "",
    },
    activeKey: {
      type: String,
      required: true,
    },
    dates: {
      type: Array,
      required: true,
    },
    selectedUser: {
      type: String,
      default: "",
    },
    parentTypeFilter: {
      type: String,
      default: "all",
    },
  },
  data() {
    return {
      data: [],
      loading: false,
      localPagination: {
        current: this.pagination.current || 1,
        pageSize: this.pagination.pageSize || 10,
        total: this.pagination.total || 0,
        showSizeChanger:
          this.pagination.showSizeChanger !== undefined
            ? this.pagination.showSizeChanger
            : true,
        showQuickJumper:
          this.pagination.showQuickJumper !== undefined
            ? this.pagination.showQuickJumper
            : true,
        showTotal:
          this.pagination.showTotal ||
          ((total, range) =>
            this.$t("all.pagination.showTotal", {
              start: range[0],
              end: range[1],
              total: total,
            })),
        pageSizeOptions: this.pagination.pageSizeOptions || [
          "10",
          "20",
          "50",
          "100",
        ],
      },
      localSearchText: this.searchText,
      localLoginStatus: "logged_in", // Default to showing logged in users
      searchTimeout: null,
    };
  },
  computed: {
    currentUserType() {
      if (this.activeKey == "1") {
        // If parent tab and filter is set, return the specific parent type
        if (this.parentTypeFilter && this.parentTypeFilter !== "all") {
          return this.parentTypeFilter; // "father", "mother", or "guardian"
        }
        return "parent"; // Return "parent" to fetch all parent types
      } else if (this.activeKey == "5") return "teacher";
      else if (this.activeKey == "6") return "admin";
      return "parent";
    },
    columns() {
      const isParentTab = this.activeKey == "1";
      return [
        ...baseColumns,
        ...(isParentTab ? parentColumns : []),
        ...statsColumns,
      ];
    },
    rows() {
      if (!(this.data && this.data.length > 0)) return [];

      return this.data.map((userData, index) => {
        // Process connection history to create detailed sessions
        const processedHistory = [];

        if (userData.connectionHistory) {
          userData.connectionHistory.forEach((dayRecord) => {
            let connects = dayRecord.connects || [];
            let disconnects = dayRecord.disconnects || [];

            // Sort arrays to ensure proper pairing
            connects = [...connects].sort((a, b) => a - b);
            disconnects = [...disconnects].sort((a, b) => a - b);

            // Create sessions by pairing connects and disconnects
            const minLength = Math.min(connects.length, disconnects.length);

            for (let i = 0; i < connects.length; i++) {
              const connectTime = connects[i];
              const disconnectTime = i < minLength ? disconnects[i] : null;

              // Calculate duration in milliseconds
              // Handles both timestamp (number) and Date object formats
              let duration = null;
              if (disconnectTime && connectTime) {
                const connectMs =
                  typeof connectTime === "number"
                    ? connectTime
                    : new Date(connectTime).getTime();
                const disconnectMs =
                  typeof disconnectTime === "number"
                    ? disconnectTime
                    : new Date(disconnectTime).getTime();

                if (disconnectMs && connectMs) {
                  const calculatedDuration = disconnectMs - connectMs;
                  if (calculatedDuration > 0) {
                    duration = calculatedDuration;
                  } else {
                    duration = 0;
                  }
                }
              }

              processedHistory.push({
                date: dayRecord.date,
                connect: connectTime,
                disconnect: disconnectTime,
                duration: duration,
              });
            }
          });
        }

        // Sort history by connection time (most recent first)
        processedHistory.sort((a, b) => b.connect - a.connect);

        return {
          key: userData.user.id + "_" + index,
          user: userData.user.name,
          fullName: userData.fullName || "",
          childName: userData.childName || "",
          totalConnections: userData.totalConnections || 0,
          totalDuration: userData.totalDurationFormatted || "0s",
          lastConnection: userData.lastConnection
            ? moment(userData.lastConnection).format("DD-MM-YYYY HH:mm:ss")
            : "--",
          connectionHistory: processedHistory,
        };
      });
    },
    tablePagination() {
      return {
        current: this.localPagination.current,
        pageSize: this.localPagination.pageSize,
        total: this.localPagination.total,
        showSizeChanger: this.localPagination.showSizeChanger,
        showQuickJumper: this.localPagination.showQuickJumper,
        showTotal: this.localPagination.showTotal,
        pageSizeOptions: this.localPagination.pageSizeOptions,
      };
    },
  },
  watch: {
    pagination: {
      handler(newVal) {
        if (newVal) {
          this.localPagination.current =
            newVal.current || this.localPagination.current;
          this.localPagination.pageSize =
            newVal.pageSize || this.localPagination.pageSize;
          this.localPagination.total =
            newVal.total !== undefined
              ? newVal.total
              : this.localPagination.total;
        }
      },
      deep: true,
    },
    searchText(newVal) {
      this.localSearchText = newVal;
    },
    activeKey() {
      this.localPagination.current = 1;
      this.fetchData();
    },
    dates: {
      handler() {
        this.localPagination.current = 1;
        this.fetchData();
      },
      deep: true,
    },
    selectedUser() {
      this.localPagination.current = 1;
      this.fetchData();
    },
    parentTypeFilter() {
      this.localPagination.current = 1;
      this.fetchData();
    },
    localLoginStatus() {
      this.localPagination.current = 1;
      this.fetchData();
    },
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    moment,
    formatDuration(milliseconds) {
      // Handle null, undefined, or NaN
      if (
        milliseconds === null ||
        milliseconds === undefined ||
        isNaN(milliseconds)
      ) {
        return "0s";
      }
      if (milliseconds <= 0) {
        return "0s";
      }

      const hours = Math.floor(milliseconds / (1000 * 60 * 60));
      const minutes = Math.floor(
        (milliseconds % (1000 * 60 * 60)) / (1000 * 60)
      );
      const seconds = Math.floor((milliseconds % (1000 * 60)) / 1000);

      const parts = [];
      if (hours > 0) parts.push(`${hours}h`);
      if (minutes > 0) parts.push(`${minutes}m`);
      if (seconds > 0 || parts.length === 0) parts.push(`${seconds}s`);

      return parts.join(" ");
    },
    async fetchData() {
      this.loading = true;
      try {
        const response = await helpers.fetchConnectedUserTable(
          this.currentUserType,
          this.selectedUser || "",
          this.dates[0],
          this.dates[1],
          this.$message,
          this.$t,
          this.localPagination.current,
          this.localPagination.pageSize,
          this.localSearchText || "",
          this.localLoginStatus || "logged_in"
        );

        this.data = response.data || [];
        const paginationInfo = response.info || {};
        if (paginationInfo.count !== undefined) {
          this.localPagination.total = paginationInfo.count;
          this.localPagination.current =
            paginationInfo.currentPage || this.localPagination.current;
        }
      } catch (error) {
        console.error("Error fetching table data:", error);
      } finally {
        this.loading = false;
      }
    },
    async updateTableData() {
      await this.fetchData();
    },
    handleTableChange(pagination, filters, sorter) {
      // Update local pagination state
      if (pagination.current !== undefined) {
        this.localPagination.current = pagination.current;
      }
      if (pagination.pageSize !== undefined) {
        this.localPagination.pageSize = pagination.pageSize;
      }

      // Emit change event to parent
      this.$emit("change", {
        current: this.localPagination.current,
        pageSize: this.localPagination.pageSize,
      });

      // Fetch new data with updated pagination
      this.fetchData();
    },
    handleSearch(value) {
      this.localSearchText = value;
      this.localPagination.current = 1;
      this.$emit("search", value);
      this.fetchData();
    },
    handleSearchChange(e) {
      this.localSearchText = e.target.value;
      this.localPagination.current = 1;
      this.$emit("search-change", e.target.value);

      // Debounce search
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = setTimeout(() => {
        this.fetchData();
      }, 500);
    },
    handleClear() {
      this.localSearchText = "";
      this.localPagination.current = 1;
      this.$emit("search", "");
      this.fetchData();
    },
    handleLoginStatusChange(e) {
      this.localLoginStatus = e.target.value;
      this.localPagination.current = 1;
      this.fetchData();
    },
  },
  beforeDestroy() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  },
};
</script>
